import { useState, useEffect } from 'react'
import { Calendar, Clock, ArrowRight } from 'lucide-react'
import { supabase } from '../config/supabase'

export default function News() {
  const [activeTab, setActiveTab] = useState('all')
  const [newsItems, setNewsItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // 查询新闻数据
        const { data: newsData, error } = await supabase
          .from('news_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')
          .order('created_at', { ascending: false })

        if (error) {
          console.error('Error fetching news:', error)
        } else {
          // 为新闻添加默认属性，确保与原代码兼容
          const enhancedNews = (newsData || []).map((news, index) => ({
            ...news,
            id: news.id,
            title: news.title || `新闻${index + 1}`,
            date: news.date || news.created_at ? new Date(news.created_at).toISOString().split('T')[0] : '2024-01-01',
            time: news.time || '12:00',
            category: news.category || 'company',
            image: news.image || news.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(news.title || 'news article')}&image_size=landscape_4_3`,
            summary: news.summary || news.content || '新闻内容...'
          }))
          setNewsItems(enhancedNews)
        }
      } catch (error) {
        console.error('Error fetching news:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  const filteredNews = activeTab === 'all' 
    ? newsItems 
    : newsItems.filter(item => item.category === activeTab)

  return (
    <section id="news" className="py-20 bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
            新闻资讯
          </h2>
          <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
            了解云净炉业的最新动态、行业资讯和技术创新
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-12">
          <div className="flex flex-col sm:flex-row rounded-lg bg-white p-1 shadow-sm w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'all' ? 'bg-primary-600 text-white' : 'text-secondary-700 hover:bg-secondary-50'}`}
            >
              全部
            </button>
            <button
              onClick={() => setActiveTab('公司动态')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-colors ${activeTab === '公司动态' ? 'bg-primary-600 text-white' : 'text-secondary-700 hover:bg-secondary-50'}`}
            >
              公司动态
            </button>
            <button
              onClick={() => setActiveTab('产品资讯')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-colors ${activeTab === '产品资讯' ? 'bg-primary-600 text-white' : 'text-secondary-700 hover:bg-secondary-50'}`}
            >
              产品资讯
            </button>
            <button
              onClick={() => setActiveTab('合作新闻')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-colors ${activeTab === '合作新闻' ? 'bg-primary-600 text-white' : 'text-secondary-700 hover:bg-secondary-50'}`}
            >
              合作新闻
            </button>
          </div>
        </div>

        {/* News Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-secondary-600 text-lg">加载中...</p>
          </div>
        ) : filteredNews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredNews.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm text-secondary-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 sm:w-4 h-3 sm:h-4" />
                      <span>{item.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 sm:w-4 h-3 sm:h-4" />
                      <span>{item.time}</span>
                    </div>
                  </div>
                  <h3 className="text-4vw sm:text-5vw font-bold text-secondary-900 mb-2 sm:mb-3 line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="news-content text-secondary-600 mb-4 sm:mb-6 line-clamp-3 text-sm sm:text-base">
                    <div dangerouslySetInnerHTML={{ __html: item.summary }} />
                  </div>

                  <a href={`/news-detail?id=${item.id}`} className="flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm sm:text-base">
                    <span>阅读更多</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-secondary-600 text-lg">暂无新闻数据</p>
          </div>
        )}

        {/* View More Button */}
        <div className="text-center mt-12">
          <a href="/news" className="inline-block px-8 py-3 border-2 border-primary-600 text-primary-600 rounded-full font-semibold hover:bg-primary-600 hover:text-white transition-colors">
            查看更多新闻
          </a>
        </div>
      </div>
    </section>
  )
}